import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { AddIcon, DownIcon, StarFilledIcon } from '@/assets';
import { TourTabBar } from '@/features/tutorial/components/TourTabBar';
import type {
  TourPage,
  TourScreenMock
} from '@/features/tutorial/types/tutorial';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import type { SVGComponent } from '@/types';

export const ILLUSTRATION_HEIGHT = 240;
const PHONE_WIDTH = 220;
const PHONE_RADIUS = 20;
const HIGHLIGHT_BORDER = 2;
const SMALL_ICON = 14;
const CHIP_SIZE = 26;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      height: ILLUSTRATION_HEIGHT,
      width: '100%',
      borderRadius: 12,
      backgroundColor: theme.colors.surfaceVariant,
      alignItems: 'center',
      justifyContent: 'flex-end',
      overflow: 'hidden'
    },
    phone: {
      width: PHONE_WIDTH,
      height: ILLUSTRATION_HEIGHT - 16,
      borderTopLeftRadius: PHONE_RADIUS,
      borderTopRightRadius: PHONE_RADIUS,
      borderWidth: 4,
      borderBottomWidth: 0,
      borderColor: theme.colors.surfaceContainerLowest,
      backgroundColor: theme.colors.surface,
      overflow: 'hidden'
    },
    screen: { flex: 1, padding: 8, gap: 6 },
    highlight: {
      borderWidth: HIGHLIGHT_BORDER,
      borderColor: theme.colors.error,
      borderRadius: 8
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      padding: 4
    },
    headerText: { flex: 1 },
    headerTitle: {
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '600',
      color: theme.colors.onSurface
    },
    headerSubtitle: {
      fontSize: 8,
      lineHeight: 10,
      color: theme.colors.onSurfaceVariant
    },
    chips: { flexDirection: 'row', gap: 6, padding: 2 },
    chip: {
      width: CHIP_SIZE,
      height: CHIP_SIZE,
      borderRadius: CHIP_SIZE / 2,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    chipActive: { backgroundColor: theme.colors.tertiaryContainer },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      padding: 6,
      borderRadius: 8,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    bars: { flex: 1, gap: 3 },
    bar: {
      height: 5,
      borderRadius: 3,
      backgroundColor: theme.colors.outlineVariant
    },
    barShort: { width: '60%' },
    sheet: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      padding: 8,
      gap: 6,
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      backgroundColor: theme.colors.surfaceContainerLowest,
      boxShadow: '0 -2px 8px rgba(0,0,0,0.15)'
    },
    sheetHandle: {
      alignSelf: 'center',
      width: 24,
      height: 3,
      borderRadius: 2,
      backgroundColor: theme.colors.outlineVariant
    },
    sheetTitle: {
      fontSize: 10,
      lineHeight: 12,
      fontWeight: '600',
      color: theme.colors.onSurface
    },
    sheetRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    avatar: {
      width: 14,
      height: 14,
      borderRadius: 7,
      backgroundColor: theme.colors.secondaryContainer
    },
    stars: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 4,
      paddingVertical: 10
    },
    fab: {
      position: 'absolute',
      right: 8,
      bottom: 8,
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primary
    }
  });

const Highlightable: FC<{
  active: boolean;
  style: object;
  children: ReactNode;
}> = ({ active, style, children }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <View style={[active ? styles.highlight : undefined, style]}>
      {children}
    </View>
  );
};

const MockIcon: FC<{ icon: SVGComponent; color: string; size: number }> = ({
  icon: Icon,
  color,
  size
}) => <Icon fill={color} color={color} width={size} height={size} />;

const MockScreen: FC<{ mock: TourScreenMock }> = ({ mock }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const {
    header,
    tabs,
    activeTab = 0,
    rows,
    sheet,
    fab,
    stars,
    highlight
  } = mock;

  return (
    <View style={styles.screen}>
      {header && (
        <Highlightable active={highlight === 'header'} style={styles.header}>
          <MockIcon icon={header.icon} color={theme.colors.primary} size={18} />
          <View style={styles.headerText}>
            <Text style={styles.headerTitle} numberOfLines={1}>
              {header.title}
            </Text>
            {header.subtitle ? (
              <Text style={styles.headerSubtitle} numberOfLines={1}>
                {header.subtitle}
              </Text>
            ) : undefined}
          </View>
          {header.expandable && (
            <MockIcon
              icon={DownIcon}
              color={theme.colors.onSurfaceVariant}
              size={14}
            />
          )}
        </Highlightable>
      )}
      {tabs && (
        <Highlightable active={highlight === 'tabs'} style={styles.chips}>
          {tabs.map((icon, index) => (
            <View
              key={index}
              style={[
                styles.chip,
                index === activeTab ? styles.chipActive : undefined
              ]}
            >
              <MockIcon
                icon={icon}
                color={theme.colors.primary}
                size={SMALL_ICON}
              />
            </View>
          ))}
        </Highlightable>
      )}
      {rows?.map((icon, index) => (
        <View key={index} style={styles.row}>
          <MockIcon
            icon={icon}
            color={theme.colors.primary}
            size={SMALL_ICON}
          />
          <View style={styles.bars}>
            <View style={styles.bar} />
            <View style={[styles.bar, styles.barShort]} />
          </View>
        </View>
      ))}
      {stars ? (
        <View style={styles.stars} testID="tour-stars">
          {Array.from({ length: stars }, (_, index) => (
            <MockIcon
              key={index}
              icon={StarFilledIcon}
              color={theme.colors.error}
              size={22}
            />
          ))}
        </View>
      ) : undefined}
      {fab && (
        <Highlightable active={highlight === 'fab'} style={styles.fab}>
          <MockIcon icon={AddIcon} color={theme.colors.onPrimary} size={16} />
        </Highlightable>
      )}
      {sheet && (
        <Highlightable active={highlight === 'sheet'} style={styles.sheet}>
          <View style={styles.sheetHandle} />
          <Text style={styles.sheetTitle} numberOfLines={1}>
            {sheet.title}
          </Text>
          {Array.from({ length: sheet.rows }, (_, index) => (
            <View key={index} style={styles.sheetRow}>
              <View style={styles.avatar} />
              <View style={styles.bars}>
                <View style={styles.bar} />
              </View>
            </View>
          ))}
        </Highlightable>
      )}
    </View>
  );
};

type Props = {
  page: TourPage;
};

/**
 * Phone frame with a stylised screen and the miniature tab bar. Replaces the
 * former static PNGs, which went stale after the navigation redesign.
 */
export const TourIllustration: FC<Props> = ({ page }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <View
      style={styles.container}
      testID={`tour-illustration-${page.key}`}
      // The illustration is purely decorative — the meaningful copy lives in
      // PageContent. Hide it from assistive tech so the tour tabs / chips are
      // not announced as if they were interactive navigation.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <View style={styles.phone}>
        <MockScreen mock={page.mock} />
        <TourTabBar active={page.tab} />
      </View>
    </View>
  );
};
